<script setup lang="ts">
import type { JournalEditionShortDTO } from '~/shared/api/client/journals/journals.dto';
import {
  getJournalEditionUrl,
  getJournalYearUrl,
} from '~/shared/lib/journal-routes.const';
import type { JournalChip } from '../lib/journal.const';
import {
  useJournalEditions,
  useJournalIssues,
  useJournalYears,
} from '../lib/useJournalData';
import JournalChips from './JournalChips.vue';
import JournalIssuesPaged from './JournalIssuesPaged.vue';

const props = defineProps<{
  edition: JournalEditionShortDTO;
  /** A year of the archive, null while the whole edition is shown */
  year?: number | null;
}>();

/**
 * The years the chips offer, with the issue count of each. Counted by the
 * endpoint rather than here: a year with no issue must never become a chip
 * that leads nowhere, and the page holds one portion of issues, not all of them
 */
const [
  { editions: reference },
  { years },
  { issues, hasMore, isLoading, loadMore },
] = await Promise.all([
  useJournalEditions(),
  useJournalYears(() => props.edition.id),
  useJournalIssues({
    edition: () => props.edition.id,
    year: () => props.year ?? undefined,
  }),
]);

const chips = computed<JournalChip[]>(() => [
  {
    key: 'all',
    label: 'Все',
    to: getJournalEditionUrl(reference.value, props.edition),
    active: !props.year,
  },
  ...years.value.map((item) => ({
    key: item.year,
    label: String(item.year),
    to: getJournalYearUrl(props.edition, item.year),
    active: item.year === props.year,
  })),
]);
</script>

<template>
  <section :class="$style.root">
    <h2 :class="$style.heading">Архив журнала {{ edition.name }}</h2>
    <JournalChips :items="chips" :class="$style.years" />
    <JournalIssuesPaged
      :issues="issues"
      :has-more="hasMore"
      :is-loading="isLoading"
      :class="$style.grid"
      @load-more="loadMore"
    />
  </section>
</template>

<style module lang="scss">
.root {
  display: flex;
  flex-direction: column;
  padding-bottom: var(--space-6x);
  background-color: var(--color-white);
}

.heading {
  @include text_h2;
  color: var(--color-black-1);
  margin: 0;
  padding: var(--space-5x) var(--space-2-5x) var(--space-3x);
  text-align: center;
}

.years {
  padding: 0 var(--space-2-5x) var(--space-3x);
}

.grid {
  margin-top: var(--space-1x);
}

@media (max-width: 767px) {
  .heading {
    @include text_h2_mobile;
  }
}

@media (max-width: 599px) {
  .heading {
    padding: var(--space-4x) var(--space-2x) var(--space-2x);
  }

  .years {
    padding: 0 var(--space-2x) var(--space-2-5x);
  }
}
</style>
